/**
 * PD-16558 — helpers for promoting the storefront's mobile app: which store
 * this device uses, whether we are already inside the app, and the phone
 * "Get the app" bar's snooze.
 */

export type StorePlatform = 'ios' | 'android' | 'other';

/**
 * The store this device installs from. iPadOS reports itself as a Mac, so a
 * "Macintosh" user agent with a touch screen counts as iOS.
 */
export function detectStorePlatform(): StorePlatform {
	if (typeof navigator === 'undefined') {
		return 'other';
	}
	const ua = navigator.userAgent;
	if (/android/i.test(ua)) {
		return 'android';
	}
	if (
		/iphone|ipad|ipod/i.test(ua) ||
		(/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
	) {
		return 'ios';
	}
	return 'other';
}

/**
 * True inside the storefront's own iOS or Android app, where promoting the
 * app would be pointless. Read from the Capacitor bridge the native shell
 * injects, the same check main.tsx uses.
 */
export function isInsideNativeApp(): boolean {
	const capacitor = (
		window as unknown as {
			Capacitor?: { isNativePlatform?: () => boolean };
		}
	).Capacitor;
	return capacitor?.isNativePlatform?.() === true;
}

const APP_BAR_SNOOZE_KEY = 'portal:get-app-bar:snoozedUntil';

/** How long closing the "Get the app" bar hides it. */
export const APP_BAR_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Whether the bar was closed within the snooze window. Storage can be
 * unavailable (private browsing, blocked site data); the bar then shows,
 * which is the safe default for a dismissible prompt.
 */
export function isAppBarSnoozed(now: number = Date.now()): boolean {
	try {
		const raw = window.localStorage.getItem(APP_BAR_SNOOZE_KEY);
		if (raw == null) {
			return false;
		}
		const until = Number(raw);
		return Number.isFinite(until) && until > now;
	} catch {
		return false;
	}
}

/** Hide the bar for the snooze window. */
export function snoozeAppBar(now: number = Date.now()): void {
	try {
		window.localStorage.setItem(
			APP_BAR_SNOOZE_KEY,
			String(now + APP_BAR_SNOOZE_MS),
		);
	} catch {
		// Storage unavailable: the bar is hidden for this page view only.
	}
}
